/**
 * Shared chrome for the v2 sheet views.
 *
 * v2's own table components (views/v2/table), given the model's periods, its check flags and
 * the info bubble of every calculated row (rowHints).
 */

import { useEffect, useMemo, type ReactNode } from 'react';
import { FinancialTable } from '../table/FinancialTable';
import {
  addFigureFlags,
  buildCheckFlags,
  CheckFlagsProvider,
} from '../table/CheckFlagsContext';
import { TableSettingsProvider } from '../table/TableSettingsContext';
import { RowHintsProvider } from '../table/RowHint';
import { accountingHint } from './rowHints';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import { V2EditLock } from './V2EditableRow';

export function V2Table({ children }: { children: ReactNode }) {
  const settings = useFinancialModelV2Store((s) => s.model?.settings);
  const isRecalculating = useFinancialModelV2Store((s) => s.isRecalculating);
  const computed = useFinancialModelV2Store((s) => s.model?.computed);
  const version = useFinancialModelV2Store((s) => s.model?.version);
  const audit = useFinancialModelV2Store((s) => s.tabAudit);
  const loadTabAudit = useFinancialModelV2Store((s) => s.loadTabAudit);

  // The audit is fetched by the Overview; a sheet opened first fetches it too, and again
  // for each version, so a corrected figure stops being flagged.
  useEffect(() => {
    void loadTabAudit();
  }, [loadTabAudit, version]);

  // A failing check marks the very cell on the very row it concerns (CheckFlagsContext).
  // And a figure no printed total vouches for, with the question that would settle it.
  const flags = useMemo(
    () =>
      addFigureFlags(
        computed,
        buildCheckFlags(computed, (audit?.tabs ?? []).flatMap((tab) => tab.failing)),
        audit?.figures ?? [],
      ),
    [computed, audit],
  );

  return (
    <TableSettingsProvider settings={settings as any} isRecalculating={isRecalculating}>
      <CheckFlagsProvider flags={flags}>
        <RowHintsProvider lookup={accountingHint}>
          <FinancialTable>{children}</FinancialTable>
        </RowHintsProvider>
      </CheckFlagsProvider>
    </TableSettingsProvider>
  );
}

/**
 * The model's balance check, shown on every sheet.
 *
 * The template displays it in cell A1 of every tab, and that is worth keeping:
 * a balance sheet that does not balance is a modelling error, and it should be
 * visible wherever you are working rather than only on the balance sheet.
 */
export function V2BalanceBanner() {
  const balanceCheck = useFinancialModelV2Store(
    (s) => s.model?.computed.balanceSheet.balanceCheck,
  );
  const saveError = useFinancialModelV2Store((s) => s.saveError);
  const clearSaveError = useFinancialModelV2Store((s) => s.clearSaveError);

  const offending = Object.entries(balanceCheck ?? {}).filter(
    ([, value]) => Math.abs(value) > 1,
  );

  if (saveError) {
    return (
      <div className="shrink-0 bg-red-50 border-b border-red-200 px-4 py-2 text-sm text-red-800 flex items-center justify-between">
        <span>
          <strong>Not saved.</strong> {saveError}
        </span>
        <button
          type="button"
          onClick={clearSaveError}
          className="text-red-700 underline hover:no-underline"
        >
          Dismiss
        </button>
      </div>
    );
  }

  if (offending.length === 0) return null;

  return (
    <div className="shrink-0 bg-amber-50 border-b border-amber-200 px-4 py-2 text-sm text-amber-900">
      <strong>Balance check failed</strong> in{' '}
      {offending.map(([period]) => period).join(', ')}. Assets less liabilities
      does not equal equity — the model has an error rather than a rounding
      difference.
    </div>
  );
}

export function V2Sheet({ children }: { children: ReactNode }) {
  return (
    <div className="h-full flex flex-col min-w-0">
      <V2BalanceBanner />
      {/* The edit lock goes on every sheet rather than on one of them, because the figure a
          user wants to correct is on whichever tab they happen to be looking at. It is also
          the answer to a question the sheet otherwise raises silently: a cell that will not
          accept a keystroke and does not say why reads as broken software. */}
      <div className="shrink-0 flex justify-end px-4 pt-3 sm:px-6">
        <V2EditLock />
      </div>
      {/* The sheet tabs ran flush to the window edge while Overview, Charts
          and AI Forecast all sit in padding, so moving between them shifted
          the left edge of the content. The gutter goes here rather than on
          each of the eight views, which is also what keeps them consistent
          when a ninth is added.

          Horizontal only: the banner above is full-bleed by design, and a top
          gutter would detach it from the sheet it belongs to. */}
      {/* `v2-sheet` restyles the shared statement rows to the baseline model's look - a
          card, quiet rules, tabular figures - in v2 only (styles/globals.css). The rows are
          v1's components too, and v1 keeps its own look. */}
      <div className="min-h-0 flex-1 px-4 pb-6 pt-2 sm:px-6">
        <div className="v2-sheet flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_2px_rgb(var(--c-black)/0.04)]">
          <V2Table>{children}</V2Table>
        </div>
      </div>
    </div>
  );
}
